import styles from './move-contracts-dialog.module.scss';
import { useContext, useState } from 'react';
import { useImmer } from 'use-immer';
import { ContactListContext } from '../../customer-context/customer-context';
import {
  Accordion, AccordionDetails, AccordionSummary, Checkbox, CheckboxLabel, CompanySearch, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, Icon, Paper,
  PrimaryButton, SnackBarContext, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@frontend-workspace/react-shared-lib';
import { IAllContracts } from '../../_models/all-contracts';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../../requestUtils';

/* eslint-disable-next-line */
export interface MoveContractsDialogProps {
  open: boolean,
  onClose: () => void,
  contracts: IAllContracts[]
}

export function MoveContractsDialog(props: MoveContractsDialogProps) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const contactList = useContext(ContactListContext);
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const contracts = props.contracts.filter(c => c.isActive);
  const [moveDate, setMoveDate] = useState<null | Date>(new Date());
  const [selectedLicenseNumbers, updateSelectedLicenseNumbers] = useImmer<number[]>([]);
  const [selectedCompanyNumber, setSelectedCompanyNumber] = useState<string | undefined>(undefined);

  const handleMoveDateChange = (newDate: Date | null) => setMoveDate(newDate);
  const handleCompanyChange = (companyNumber: string) => setSelectedCompanyNumber(companyNumber);

  const handleMoveContracts = () => {
    const fullContracts = contracts.filter(c => c.licenses.length > 0 && c.licenses.every((license: any) => selectedLicenseNumbers.includes(license.licenseNumber)));
    const contractNumbersOfFullContracts = fullContracts.map(c => c.contractNumber);
    const partialContracts = contracts.filter(c => c.licenses.some((license: any) => selectedLicenseNumbers.includes(license.licenseNumber)) &&
      !contractNumbersOfFullContracts.includes(c.contractNumber));
    const partialBody = partialContracts.map(c => {
      return {
        contractNumber: c.contractNumber,
        licenseNumbers: c.licenses.filter((license: any) => selectedLicenseNumbers.includes(license.licenseNumber)).map((l: any) => l.licenseNumber)
      }
    });
    const body = {
      activationDate: moveDate,
      contractNumbers: contractNumbersOfFullContracts,
      partialContracts: partialBody
    };
    if (selectedCompanyNumber) {
      makeRequest(`companies/${selectedCompanyNumber}/action/moveContracts/`, 'PUT', body).then(result => {
        setSnackbarMessage({ content: t("moveContracts.movingContracts"), type: 'info' });
        props.onClose();
      });
    } else {
      setSnackbarMessage({ content: t("moveContracts.selectCompanyYouWantToMoveTo"), type: 'error' });
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="md">
      <DialogTitle className='mb-4'>
        {t("standard.moveContracts")}
      </DialogTitle>
      <DialogContent>
        <div className='mb-5'>
          <DatePicker
            inputFormat="dd.MM.yyyy"
            label={t("standard.dateOfMove")}
            value={moveDate}
            onChange={handleMoveDateChange}
          />
        </div>
        <Typography
          variant="subtitle4"
          component="div"
          className='mb-4'>
          {t("moveContracts.hereYouCanSelectContractAndLicensesYouWantToMove")}
        </Typography>
        {contracts.map((contract, index) => {
          const handleContractSelect = (event: any, checked: boolean) => {
            updateSelectedLicenseNumbers(draft => {
              const licensesInContract = contract.licenses.map((i: any) => i.licenseNumber);
              draft = draft.filter(number => !licensesInContract.includes(number));
              if (checked) draft = draft.concat(licensesInContract);
              return draft;
            })
          };
          return (
            <div className='mb-3' key={contract.contractNumber}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}>
                  <CheckboxLabel
                    key={contract.contractNumber}
                    label={`${t("standard.contractNo")} ${contract.contractNumber}`}
                    value={contract.licenses && contract.licenses.every((license: any) => selectedLicenseNumbers.includes(license.licenseNumber))}
                    onChange={handleContractSelect}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper}>
                    <Table sx={{ width: '100%' }} size="small" padding='none'>
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell>{t("standard.licenseNumber")}</TableCell>
                          <TableCell>{t("standard.productName")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contract.licenses && contract.licenses.map((license) => {
                          const handleSelectLicense = (event: any, checked: boolean) => {
                            checked ?
                              updateSelectedLicenseNumbers(draft => draft.concat([license.licenseNumber])) :
                              updateSelectedLicenseNumbers(draft => draft.filter(number => number !== license.licenseNumber));
                          };
                          return <TableRow
                            key={license.id} hover
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell>
                              <Checkbox
                                checked={selectedLicenseNumbers.includes(license.licenseNumber)}
                                onChange={handleSelectLicense}
                              />
                            </TableCell>
                            <TableCell>{license.licenseNumber}</TableCell>
                            <TableCell>{license.relationProduct?.productName}</TableCell>
                          </TableRow>
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </div>
          );
        })}
        <div className='mb-3'>
          <label>{t("standard.customerNumber")}</label>
          <CompanySearch
            selectedCompanyNumber={selectedCompanyNumber}
            companyList={contactList}
            onChange={handleCompanyChange} />
        </div>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleMoveContracts}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  )
}

export default MoveContractsDialog;